/**
 * =========================================================
 * GOOGLE ANALYTICS 4 - HEXXATECH
 * =========================================================
 *
 * Measurement ID:
 * G-6WNN3L8YQW
 *
 * This utility is used to track custom events such as:
 * - WhatsApp clicks
 * - Phone clicks
 * - Email clicks
 * - Contact form submissions
 * - Case study views
 * - Service views
 * - Quote requests
 * =========================================================
 */

const GA_MEASUREMENT_ID = "G-6WNN3L8YQW";

/**
 * Track a custom GA4 event
 *
 * Example:
 *
 * trackEvent("whatsapp_click", {
 *   location: "header",
 * });
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.gtag !== "function") {
    console.warn(
      `Google Analytics is not available. Event "${eventName}" was not tracked.`
    );
    return;
  }

  window.gtag("event", eventName, {
    ...parameters,
  });
};

/**
 * Track page views manually.
 *
 * Useful for React Router SPA navigation.
 */
export const trackPageView = (path) => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.gtag !== "function") {
    return;
  }

  window.gtag("config", GA_MEASUREMENT_ID, {
    page_path: path,
  });
};

/**
 * Generic custom event helper used across the site.
 * This keeps older imports compatible with the GA4 utility.
 */
export const trackEventCustom = (eventName, parameters = {}) => {
  trackEvent(eventName, parameters);
};

/**
 * WhatsApp Click
 */
export const trackWhatsAppClick = (location = "unknown") => {
  trackEvent("whatsapp_click", {
    location,
  });
};

/**
 * Phone Click
 */
export const trackPhoneClick = (location = "unknown") => {
  trackEvent("phone_click", {
    location,
  });
};

/**
 * Email Click
 */
export const trackEmailClick = (location = "unknown") => {
  trackEvent("email_click", {
    location,
  });
};

/**
 * Contact Form Submission
 *
 * Only call this after the form has successfully
 * submitted.
 */
export const trackContactFormSubmit = (
  formName = "contact"
) => {
  trackEvent("contact_form_submit", {
    form_name: formName,
  });
};

/**
 * Quote Request
 */
export const trackQuoteRequest = (location = "unknown") => {
  trackEvent("quote_request", {
    location,
  });
};

/**
 * Case Study View
 */
export const trackCaseStudyView = (caseStudy) => {
  trackEvent("case_study_view", {
    case_study: caseStudy,
  });
};

/**
 * Service View
 */
export const trackServiceView = (service) => {
  trackEvent("service_view", {
    service,
  });
};

export default {
  trackEvent,
  trackEventCustom,
  trackPageView,
  trackWhatsAppClick,
  trackPhoneClick,
  trackEmailClick,
  trackContactFormSubmit,
  trackQuoteRequest,
  trackCaseStudyView,
  trackServiceView,
};